<template>
  <div class="admin-email-view">
    <div class="page-header">
      <h1 class="page-title">
        Send Email
        <span class="page-subtitle"
          >Compose and send an email to users from the support address</span
        >
      </h1>
    </div>

    <div class="content">
      <!-- Origin / config summary -->
      <div class="setting-card mb-4">
        <div class="setting-head">
          <v-icon color="primary" class="mr-2">mdi-email-cog</v-icon>
          <div>
            <h2 class="setting-title">Sending from</h2>
            <p class="setting-desc">
              All emails are sent from the support address so that replies come
              straight back to the support inbox.
            </p>
          </div>
        </div>
        <div class="from-line">
          <v-icon small left>mdi-email-outline</v-icon>
          <strong>{{ fromAddress }}</strong>
          <v-chip
            v-if="config"
            class="ml-3"
            :color="config.testing_mode ? 'warning' : 'success'"
            small
            label
          >
            {{ config.testing_mode ? "Testing mode" : "Production" }}
          </v-chip>
        </div>
        <v-alert
          v-if="config && config.testing_mode"
          type="warning"
          dense
          class="mt-3 mb-0"
        >
          <strong>Testing mode is on.</strong> Emails will actually be sent from
          <code>onboarding@resend.dev</code>, not the support address. Turn off
          <code>EMAIL_TESTING_MODE</code> in the backend to send from
          <code>{{ supportAddress }}</code
          >.
        </v-alert>
        <v-alert
          v-if="config && !config.api_key_configured"
          type="error"
          dense
          class="mt-3 mb-0"
        >
          Resend API key is not configured - sending will fail.
        </v-alert>
      </div>

      <!-- Compose -->
      <div class="setting-card">
        <div class="setting-head">
          <v-icon color="primary" class="mr-2">mdi-email-edit-outline</v-icon>
          <div>
            <h2 class="setting-title">Compose</h2>
            <p class="setting-desc">
              Pick recipients from your users or type addresses manually, then
              write your message.
            </p>
          </div>
        </div>

        <v-combobox
          v-model="recipients"
          :items="userEmailItems"
          :loading="loadingUsers"
          label="Recipients"
          hint="Select users or type an email address and press Enter"
          persistent-hint
          multiple
          chips
          deletable-chips
          small-chips
          outlined
          class="mb-2"
        ></v-combobox>

        <div class="recipient-tools mb-4">
          <v-btn
            text
            small
            :disabled="loadingUsers || !userEmails.length"
            @click="selectAllUsers"
          >
            <v-icon small left>mdi-account-multiple-check</v-icon>
            Select all users ({{ userEmails.length }})
          </v-btn>
          <v-btn
            text
            small
            :disabled="!recipients.length"
            @click="recipients = []"
          >
            <v-icon small left>mdi-close</v-icon>
            Clear
          </v-btn>
          <v-spacer></v-spacer>
          <span class="count-note">{{ recipients.length }} selected</span>
        </div>

        <v-text-field
          v-model="subject"
          label="Subject"
          outlined
          dense
          class="mb-2"
          :counter="200"
        ></v-text-field>

        <v-textarea
          v-model="body"
          outlined
          auto-grow
          rows="12"
          label="Message"
          hide-details="auto"
          class="body-input"
          :placeholder="
            isHtml
              ? 'Write HTML here...'
              : 'Write your message here. Plain text - line breaks are preserved.'
          "
        ></v-textarea>

        <div class="setting-actions">
          <v-switch
            v-model="isHtml"
            label="Send as HTML"
            dense
            hide-details
            class="mt-0"
          ></v-switch>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :loading="sending"
            :disabled="!canSend"
            @click="confirmDialog = true"
          >
            <v-icon left>mdi-send</v-icon>
            Send{{ recipients.length ? " (" + recipients.length + ")" : "" }}
          </v-btn>
        </div>
      </div>

      <!-- Results -->
      <div v-if="lastResult" class="setting-card mt-4">
        <div class="setting-head">
          <v-icon
            :color="lastResult.failed ? 'warning' : 'success'"
            class="mr-2"
            >mdi-email-check-outline</v-icon
          >
          <div>
            <h2 class="setting-title">
              Sent {{ lastResult.sent }} / {{ lastResult.total }}
              <span v-if="lastResult.failed" class="fail-count"
                >· {{ lastResult.failed }} failed</span
              >
            </h2>
            <p class="setting-desc">From {{ lastResult.from_address }}</p>
          </div>
        </div>
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in lastResult.results" :key="r.email">
                <td>{{ r.email }}</td>
                <td>
                  <v-icon small :color="r.success ? 'success' : 'error'">
                    {{ r.success ? "mdi-check-circle" : "mdi-close-circle" }}
                  </v-icon>
                </td>
                <td class="detail-cell">{{ r.message }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </div>

    <!-- Confirm dialog -->
    <v-dialog v-model="confirmDialog" max-width="440">
      <v-card>
        <v-card-title class="headline">Send email?</v-card-title>
        <v-card-text>
          You're about to send "<strong>{{ subject }}</strong
          >" to <strong>{{ recipients.length }}</strong> recipient{{
            recipients.length === 1 ? "" : "s"
          }}
          from <strong>{{ fromAddress }}</strong
          >. This cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed @click="send">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="toast.show"
      :timeout="toast.timeout"
      :color="toast.color"
      bottom
      right
    >
      {{ toast.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="toast.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import api from "@shared/services/api";
import appConfig from "@/config";

export default {
  name: "AdminEmailComposeView",
  data() {
    return {
      config: null,
      supportAddress: appConfig.SUPPORT_EMAIL,
      loadingUsers: false,
      sending: false,
      confirmDialog: false,
      userEmails: [],
      recipients: [],
      subject: "",
      body: "",
      isHtml: false,
      lastResult: null,
      toast: { show: false, message: "", color: "success", timeout: 4000 }
    };
  },
  computed: {
    fromAddress() {
      if (this.config && this.config.from_address) {
        return this.config.from_address;
      }
      return this.supportAddress;
    },
    userEmailItems() {
      return this.userEmails.map(u => ({
        text: u.name ? u.name + " <" + u.email + ">" : u.email,
        value: u.email
      }));
    },
    canSend() {
      return (
        this.recipients.length > 0 &&
        this.subject.trim().length > 0 &&
        this.body.trim().length > 0 &&
        !this.sending
      );
    }
  },
  created() {
    this.fetchConfig();
    this.fetchUsers();
  },
  methods: {
    async fetchConfig() {
      try {
        this.config = await api.adminGetEmailConfig();
      } catch (e) {
        // Non-fatal - the compose form still works.
        this.showToast("Could not load email config", "warning");
      }
    },
    async fetchUsers() {
      this.loadingUsers = true;
      try {
        const data = await api.adminListUsers();
        const list = Array.isArray(data) ? data : [];
        this.userEmails = list
          .filter(u => u && u.email)
          .map(u => ({ email: u.email, name: u.name || "" }));
      } catch (e) {
        this.showToast("Failed to load users", "error");
        this.userEmails = [];
      } finally {
        this.loadingUsers = false;
      }
    },
    selectAllUsers() {
      const set = new Set(this.recipients.map(r => this.emailOf(r)));
      this.userEmails.forEach(u => {
        if (!set.has(u.email)) {
          this.recipients.push(u.email);
          set.add(u.email);
        }
      });
    },
    emailOf(entry) {
      // Combobox entries may be plain strings or { text, value } objects.
      if (entry && typeof entry === "object") {
        return entry.value || entry.text || "";
      }
      return entry;
    },
    async send() {
      this.confirmDialog = false;
      const recipients = this.recipients
        .map(r => this.emailOf(r))
        .map(e => (e || "").trim())
        .filter(Boolean);

      if (!recipients.length) {
        this.showToast("No recipients", "error");
        return;
      }

      this.sending = true;
      this.lastResult = null;
      try {
        const result = await api.adminSendBulkEmail({
          recipients,
          subject: this.subject.trim(),
          body: this.body,
          is_html: this.isHtml
        });
        this.lastResult = result;
        if (result.failed) {
          this.showToast(
            "Sent " + result.sent + " of " + result.total + " emails",
            "warning"
          );
        } else {
          this.showToast("Sent " + result.sent + " emails", "success");
        }
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.detail) ||
          "Failed to send email";
        this.showToast(msg, "error");
      } finally {
        this.sending = false;
      }
    },
    showToast(message, color = "success") {
      this.toast.message = message;
      this.toast.color = color;
      this.toast.show = true;
    }
  }
};
</script>

<style scoped>
.admin-email-view {
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--bg-primary);
  padding-bottom: 40px;
}

.page-header {
  background: var(--page-header-bg);
  border-bottom: 1.5px solid var(--page-header-border);
  padding: 12px 32px;
}

.page-title {
  color: var(--text-heading);
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.5px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-subtitle {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: 400;
}

.content {
  padding: 32px;
  max-width: 900px;
}

.setting-card {
  background: var(--bg-card);
  border: 1.5px solid var(--border-color);
  border-radius: 12px;
  padding: 24px;
}

.setting-head {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
}

.setting-title {
  color: var(--text-heading);
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 4px;
}

.setting-desc {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
  max-width: 640px;
}

.from-line {
  color: var(--text-heading);
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

.recipient-tools {
  display: flex;
  align-items: center;
}

.count-note {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
}

.body-input {
  font-size: 0.95rem;
}

.setting-actions {
  display: flex;
  align-items: center;
  margin-top: 16px;
}

.fail-count {
  color: var(--warning, #fb8c00);
  font-weight: 600;
}

.detail-cell {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
}

.mb-4 {
  margin-bottom: 16px;
}

.mt-4 {
  margin-top: 16px;
}
</style>
